import React from 'react';
import {Navigate, Outlet, useLocation} from "react-router";
import {Box, Divider} from "@mui/material";
import LoadingInTheMiddle from "../../../components/LoadingInTheMiddle";
import {useReportStore} from "../useReportStore";
import ReportHeader from "../reportEditorPage/reportHeader/ReportHeader";
import {useSession} from "../../../firebase/UserProvider";
import {useReportLoader} from "./useReportLoader";
import {useDocumentTitle} from "./useDocumentTitle";
import {useReportAccess} from "./useReportAccess";
import {AccessDenied, AccessError, ReportNotFound} from "./AccessMessages";
import ApprovedProductPreviewPage from "../previewPage/ApprovedProductPreviewPage";
import GlobalGenerateInitialVersionModal from "../disclosureEditorPage/leftPanel/GlobalGenerateInitialVersionModal";
import {useInitSourcePackageBuilder} from "../hooks/useInitSourcePackageBuilder";
import {useInitSourcesExplorer} from '../hooks/useInitSourcesExplorer';
import {useInitSourcesFileViewer} from '../hooks/useInitSourcesFileViewer';
import SourcesFileViewerModal from '../reportEditorPage/leftPanel/components/SourcesFileViewerModal';
import {SourcesSectionPortalProvider} from '../reportEditorPage/leftPanel/components/SourcesSectionReversePortal';
import EvidenceModal from '../../../components/EvidenceModal';
import MapEditorPortal from '../../../components/map/MapEditorPortal';

/**
 * ReportPageLayout - Unified layout component for all report types
 *
 * Handles:
 * - Loading report data
 * - Auth session management
 * - Access control
 * - Route redirects based on status and role
 *
 * Renders:
 * - ApprovedProductPreviewPage for approved products (no child routes)
 * - Outlet for draft reports (edit/preview child routes)
 *
 * Modals are rendered at this level to prevent re-renders when editor content changes.
 * URL params are read once on mount and synced back on state changes.
 */
const ReportPageLayout = () => {
    const rteRef = React.useRef(null);
    const location = useLocation();
    const currentReportId = useReportStore(s => s.id);
    const status = useReportStore(s => s.status);

    // Auth session
    const {user, loading: sessionLoading} = useSession() || {user: null, loading: false};

    // Load report data and sync with backend
    const {isLoading, notFound, accessDenied, reportId} = useReportLoader(user);

    // Manage browser document title
    useDocumentTitle();

    // Check user access permissions
    const {role, isViewer, allowedRole, accessLoading, accessError} = useReportAccess(reportId, user);

    // Initialize modal stores from URL params (read once, write back on changes)
    useInitSourcePackageBuilder();
    useInitSourcesExplorer();
    useInitSourcesFileViewer();

    // Derived state
    const isApproved = status === 'approved';
    const isIndexRoute = location.pathname === `/products/${reportId}`;

    // Loading states - consolidated check
    if (notFound) {
        return <ReportNotFound/>;
    }

    if (accessDenied) {
        return <AccessDenied/>;
    }

    if (isLoading || currentReportId !== reportId || sessionLoading || accessLoading) {
        return <LoadingInTheMiddle/>;
    }

    // Error states
    if (accessError) {
        console.error('Access subscription error:', accessError);
        return <AccessError/>;
    }

    if (!allowedRole) {
        return <AccessDenied/>;
    }

    // Approved reports: show read-only view, no child routes
    if (isApproved) {
        if (!isIndexRoute) {
            return <Navigate to={{pathname: `/products/${reportId}`, search: location.search}} replace/>;
        }

        return (
            <>
                {/*
                 * #report-layout-area: stacking context for SourcesFileViewerModal
                 * (covers full layout including header).
                 * #report-content-area: stacking context for EvidenceModal
                 * (covers content only, below the header).
                 * isolation:isolate on both containers prevents their portaled
                 * modal z-indices from competing with CommunicationHub (z-index:1400)
                 * in the root stacking context.
                 */}
                <Box
                    id="report-layout-area"
                    sx={{position: 'relative', isolation: 'isolate', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}}
                >
                    <ReportHeader rteRef={rteRef} isViewer={true} role={role}/>
                    <Divider/>
                    <Box
                        id="report-content-area"
                        sx={{position: 'relative', isolation: 'isolate', flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}
                    >
                        <ApprovedProductPreviewPage/>
                    </Box>
                </Box>
                <EvidenceModal/>
            </>
        );
    }

    // Draft reports: handle routing to edit/preview
    if (isIndexRoute) {
        const targetRoute = isViewer ? 'preview' : 'edit';
        return <Navigate to={targetRoute} replace/>;
    }

    // Redirect viewers away from edit route
    if (isViewer && !location.pathname.endsWith('/preview')) {
        return <Navigate to="preview" replace/>;
    }

    // Some child routes (e.g. /mentions) need a scrollable content area instead of
    // the default overflow:hidden required by the split-pane editor layout.
    const isScrollableChildRoute = location.pathname.endsWith('/mentions');

    return (
        <SourcesSectionPortalProvider>
            {/*
             * #report-layout-area: stacking context for SourcesFileViewerModal
             * (covers full layout including header).
             * #report-content-area: stacking context for EvidenceModal
             * (covers content only, below the header).
             * isolation:isolate on both containers prevents their portaled
             * modal z-indices from competing with CommunicationHub (z-index:1400)
             * in the root stacking context.
             */}
            <Box
                id="report-layout-area"
                sx={{position: 'relative', isolation: 'isolate', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}}
            >
                <ReportHeader rteRef={rteRef} isViewer={isViewer} role={role}/>
                <Divider/>
                <Box
                    id="report-content-area"
                    sx={{
                        position: 'relative',
                        isolation: 'isolate',
                        flex: 1,
                        minHeight: 0,
                        overflow: isScrollableChildRoute ? 'auto' : 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Outlet/>
                </Box>
            </Box>

            {/* Modals at layout level to prevent re-renders when editor content changes */}
            <EvidenceModal/>
            <SourcesFileViewerModal/>
            <GlobalGenerateInitialVersionModal/>
            {/* ticket 11 (layer-panel-audit): thread the same viewer/editor signal already used
                to gate ReportHeader/routing above into the map editor, so LayerPanel disables
                its edit affordances (rename/delete/reorder/status-toggle) for read-only users. */}
            <MapEditorPortal isReadOnly={isViewer}/>
        </SourcesSectionPortalProvider>
    );
};

export default ReportPageLayout;
